import { Edge } from '@xyflow/react';

export type HoverTarget = { kind: 'node' | 'edge'; id: string };

export type Highlight = { nodeIds: Set<string>; edgeIds: Set<string> };

type Link = { edgeId: string; nodeId: string };

export type GraphAdjacency = {
  inputs: Map<string, Link[]>;
  outputs: Map<string, Link[]>;
  endpoints: Map<string, { source: string; target: string }>;
};

const addLink = (map: Map<string, Link[]>, key: string, link: Link) => {
  const existing = map.get(key);
  if (existing) existing.push(link);
  else map.set(key, [link]);
};

export const buildAdjacency = (edges: Edge[]): GraphAdjacency => {
  const adjacency: GraphAdjacency = { inputs: new Map(), outputs: new Map(), endpoints: new Map() };
  for (const edge of edges) {
    addLink(adjacency.inputs, edge.target, { edgeId: edge.id, nodeId: edge.source });
    addLink(adjacency.outputs, edge.source, { edgeId: edge.id, nodeId: edge.target });
    adjacency.endpoints.set(edge.id, { source: edge.source, target: edge.target });
  }
  return adjacency;
};

const walk = (start: string, links: Map<string, Link[]>, highlight: Highlight) => {
  const stack = [start];
  const visited = new Set([start]);
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) break;
    for (const link of links.get(current) ?? []) {
      highlight.edgeIds.add(link.edgeId);
      highlight.nodeIds.add(link.nodeId);
      if (visited.has(link.nodeId)) continue;
      visited.add(link.nodeId);
      stack.push(link.nodeId);
    }
  }
};

export const computeHighlight = (adjacency: GraphAdjacency, hovered: HoverTarget): Highlight => {
  const highlight: Highlight = { nodeIds: new Set(), edgeIds: new Set() };

  if (hovered.kind === 'edge') {
    const ends = adjacency.endpoints.get(hovered.id);
    if (!ends) return highlight;
    highlight.edgeIds.add(hovered.id);
    highlight.nodeIds.add(ends.source);
    highlight.nodeIds.add(ends.target);
    return highlight;
  }

  highlight.nodeIds.add(hovered.id);
  walk(hovered.id, adjacency.inputs, highlight);
  walk(hovered.id, adjacency.outputs, highlight);
  return highlight;
};
